import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import type { TFunction } from "i18next";
import { NS } from "../../../U2103";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000CheckBox from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000CheckBoxBase from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBoxBase";
import { useEffect, useState } from "react";
import U2000PasswordProgressionPopup from "../../../../u2000Core/components/popup/U2000PasswordProgressionPopup";
import U2000BasicDialog from "../../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import useU2000GoogleSsoStatus from "../../../../u2000Core/hooks/authentification/U2000UseGoogleSsoStatus";
import { U2000FormatDateTime } from "../../../../u2000Core/tools/U2000DateTimeHelpers";
import type { U2103User } from "../../../U2103Service";

interface U2103UserComponentProps<T extends Record<string, any>> {
  formStateHandlers: U2000FormStateHandlers<T>;
  t: TFunction;
}

function U2103UserBody<T extends Record<string, any>>({ formStateHandlers, t }: U2103UserComponentProps<T>) {
  const resetPwd = "resetPwd" in formStateHandlers.getCustomAttributes();
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [ssoConfirmOpen, setSsoConfirmOpen] = useState(false);
  const [googleSsoAvailable, setGoogleSsoAvailable] = useState<boolean | null>(null);
  const doGetGoogleSsoStatus = useU2000GoogleSsoStatus();

  const data = formStateHandlers.getData() as U2103User;
  const oldData = formStateHandlers.getOldData() as U2103User | null;
  const editMode = formStateHandlers.getFormEditMode();
  const ssoEnabled = Boolean(data?.ssoEnabled);
  const previousSsoEnabled = Boolean(oldData?.ssoEnabled);
  const disablingSso = editMode === U2000FormStateEnumEditMode.EDIT && previousSsoEnabled && !ssoEnabled;

  const showPasswordField =
    ((editMode === U2000FormStateEnumEditMode.ADD || resetPwd) && !ssoEnabled) || disablingSso;

  useEffect(() => {
    let cancelled = false;
    doGetGoogleSsoStatus()
      .then((result) => {
        if (!cancelled) {
          setGoogleSsoAvailable(result.googleSsoAvailable === true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGoogleSsoAvailable(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chargement unique au montage
  }, []);

  const applySsoEnabled = (checked: boolean) => {
    formStateHandlers.setData({
      ...data,
      ssoEnabled: checked,
      password: checked ? "" : data.password,
      unlinkGoogleIdentity: !checked && previousSsoEnabled ? true : false,
    } as T);
  };

  const handleSsoCheckChange = (checked: boolean) => {
    if (checked && !ssoEnabled) {
      setSsoConfirmOpen(true);
      return;
    }
    applySsoEnabled(checked);
  };

  const googleLinkedAt = data?.googleLinkedAt;
  const googleLinkedLabel = googleLinkedAt
    ? t("lblGoogleLinkedAt", { ns: NS, date: U2000FormatDateTime(googleLinkedAt) })
    : t("lblGoogleNotLinked", { ns: NS });

  return (
    <div className="flex h-full items-stretch gap-4">
      <div className="flex flex-[66_66_0%] flex-col gap-4">
        <U2000Panel title={t("lblGeneralInfo", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col items-start justify-start">
          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputText
                fieldName="username"
                label={t("lblUsername", { ns: NS })}
                formStateHandlers={formStateHandlers}
                readonly={editMode === U2000FormStateEnumEditMode.EDIT}
              />
            </div>
            <div className="flex-1">
              {showPasswordField && (
                <div className="relative">
                  <U2000InputText
                    fieldName="password"
                    label={
                      disablingSso
                        ? t("lblPasswordOnSsoDisable", { ns: NS })
                        : t("lblPassword", { ns: NS })
                    }
                    formStateHandlers={formStateHandlers}
                    onFocus={() => setShowPasswordPopup(true)}
                    onBlur={() => setShowPasswordPopup(false)}
                  />
                  <U2000PasswordProgressionPopup password={data?.password || ""} show={showPasswordPopup} position="BOTTOM_CENTER" />
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputText fieldName="firstName" label={t("lblFirstName", { ns: NS })} formStateHandlers={formStateHandlers} readonly={resetPwd} />
            </div>
            <div className="flex-1">
              <U2000InputText fieldName="lastName" label={t("lblLastName", { ns: NS })} formStateHandlers={formStateHandlers} readonly={resetPwd} />
            </div>
          </div>
          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputText fieldName="email" label={t("lblEmail", { ns: NS })} formStateHandlers={formStateHandlers} readonly={resetPwd} />
            </div>
          </div>
        </U2000Panel>
      </div>

      <div className="flex flex-[34_34_0%]">
        <U2000Panel title={t("lblConnectInfo", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col items-start justify-start">
          <div className="mt-3 flex w-full justify-start gap-2 px-2">
            <div className="mt-5 flex-1">
              <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="active" label={t("lblChkActive", { ns: NS })} readonly={resetPwd} />
            </div>
            <div className="flex-2">
              <U2000InputText fieldName="lastLogin" label={t("lbllastLogin", { ns: NS })} formStateHandlers={formStateHandlers} readonly={true} />
            </div>
          </div>
          <div className="mt-3 flex w-full justify-start gap-2 px-2">
            <div className="mt-5 flex-1">
              <U2000CheckBox formStateHandlers={formStateHandlers} fieldName="locked" label={t("lblChkLocked", { ns: NS })} />
            </div>
            <div className="flex-2">
              <U2000InputText type="number" fieldName="nbOfFailedAttempts" label={t("lblNbOfFailedAttempts", { ns: NS })} formStateHandlers={formStateHandlers} readonly={true} />
            </div>
          </div>
          <div className="mt-3 flex w-full justify-start gap-2 px-2">
            <div className="mt-5 flex-1">
              <U2000CheckBox
                formStateHandlers={formStateHandlers}
                fieldName="changePasswordOnLogin"
                label={t("lblChkChangePasswordOnLogin", { ns: NS })}
                readonly={ssoEnabled || resetPwd}
              />
            </div>
          </div>

          {/* BL-0520 — Connexion Google Workspace */}
          <div className="mt-4 w-full border-t border-gray-200 px-2 pt-4">
            {googleSsoAvailable === false && (
              <div className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {t("msgGoogleSsoUnavailable", { ns: NS })}
              </div>
            )}
            <U2000CheckBoxBase
              fieldName="ssoEnabled"
              label={t("lblChkSsoEnabled", { ns: NS })}
              value={ssoEnabled}
              setValue={handleSsoCheckChange}
              readonly={resetPwd || formStateHandlers.getFormState() === U2000FormStateEnumState.READ}
            />
            {editMode === U2000FormStateEnumEditMode.EDIT && (
              <div className="mt-2 text-sm text-gray-600">{googleLinkedLabel}</div>
            )}
            {disablingSso && (
              <div className="mt-2 text-sm text-amber-800">{t("msgSsoDisablePasswordRequired", { ns: NS })}</div>
            )}
          </div>
        </U2000Panel>
      </div>

      <U2000BasicDialog
        isOpen={ssoConfirmOpen}
        onClose={() => setSsoConfirmOpen(false)}
        title={t("dlgSsoEnableTitle", { ns: NS })}
        width="small"
      >
        <div className="flex flex-col gap-4 px-2 pb-2">
          <p className="text-sm text-gray-700">{t("dlgSsoEnableBody", { ns: NS })}</p>
          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <U2000ActionBtn
              label={t("btnCancel", { ns: "common" })}
              variant="secondary"
              handleClick={() => setSsoConfirmOpen(false)}
            />
            <U2000ActionBtn
              label={t("dlgSsoEnableConfirm", { ns: NS })}
              handleClick={() => {
                applySsoEnabled(true);
                setSsoConfirmOpen(false);
              }}
            />
          </div>
        </div>
      </U2000BasicDialog>
    </div>
  );
}

export default U2103UserBody;
